/**
 * Accounting link prompts — shared, client-safe contract.
 *
 * Providers link clients (invoicing) and staff (payroll) to their connected
 * accounting system so that invoice and pay-run pushes can find the right
 * contact or employee. A missing link holds money and pushes only: it never
 * blocks registering a client, adding a worker, scheduling, clock-in, visit
 * capture or clock-out (docs/source-of-truth/care-delivery-non-compromise.md).
 *
 * The UI says "Invoicing link" and "Payroll link" and names the provider only
 * as the connected system (for example "Linked to Xero contact …"). Xero is the
 * only provider wired today, so the provider label is carried as data.
 */

export type AccountingProviderKind = 'XERO';

export const ACCOUNTING_PROVIDER_LABELS: Record<AccountingProviderKind, string> = {
  XERO: 'Xero',
};

/**
 * Roles that may see and act on the Invoicing link card. Mirrors FINANCE_ROLES in
 * lib/integrations/xero/ar-server-actions.ts (confirmContactMapping and
 * createContactInXero); a source test keeps the two lists equal.
 */
export const INVOICING_LINK_ROLES = ['OWNER', 'TENANT_ADMIN', 'FINANCE_OFFICER', 'SYSTEM_ADMIN'];

/**
 * Roles that may see and act on the Payroll link card. Mirrors FINANCE_ROLES in
 * lib/integrations/xero/server-actions.ts (confirmEmployeeMapping); a source
 * test keeps the two lists equal.
 */
export const PAYROLL_LINK_ROLES = [
  'OWNER',
  'TENANT_ADMIN',
  'FINANCE_OFFICER',
  'PAYROLL_OFFICER',
  'SYSTEM_ADMIN',
];

export function canManageInvoicingLink(role: string | null | undefined): boolean {
  return INVOICING_LINK_ROLES.includes(String(role ?? '').toUpperCase());
}

export function canManagePayrollLink(role: string | null | undefined): boolean {
  return PAYROLL_LINK_ROLES.includes(String(role ?? '').toUpperCase());
}

/** Anchor of the Invoicing link card on the client record Budget tab. */
export const INVOICING_LINK_ANCHOR = 'invoicing-link';
/** Anchor of the Payroll link card on the worker Pay & EA profile. */
export const PAYROLL_LINK_ANCHOR = 'payroll-link';

export function clientInvoicingLinkHref(clientId: string): string {
  const id = encodeURIComponent(clientId);
  return `/clients/${id}?tab=budget&panel=${INVOICING_LINK_ANCHOR}#${INVOICING_LINK_ANCHOR}`;
}

export function workerPayrollLinkHref(staffId: string): string {
  return `/people/workers/${encodeURIComponent(staffId)}/pay-profile#${PAYROLL_LINK_ANCHOR}`;
}

export type AccountingWizardStep =
  | 'contacts'
  | 'employees'
  | 'account-codes'
  | 'tax-rates'
  | 'operating-units'
  | 'mapping-health';

const WIZARD_ROOT = '/settings/integrations/xero/wizard';

/** Deep link to one setup wizard step, keeping the chosen organisation. */
export function accountingWizardStepHref(
  step: AccountingWizardStep,
  connectionId?: string | null,
): string {
  const href = `${WIZARD_ROOT}/${step}`;
  return connectionId ? `${href}?connectionId=${encodeURIComponent(connectionId)}` : href;
}

export const ACCOUNTING_LINK_HEALTH_HREF = accountingWizardStepHref('mapping-health');

/**
 * Durable registration follow-up. The registration follow-up router matches
 * "accounting system" and sends it to the Invoicing link card.
 */
export const INVOICING_LINK_FOLLOW_UP_MESSAGE =
  'Optional: link this client to your accounting system so their invoices can be sent. Care and scheduling are not affected.';

export interface AccountingLinkOption {
  id: string;
  label: string;
}

export type ContactMatchMethod = 'AUTO_EMAIL' | 'AUTO_NAME' | 'MANUAL' | 'CREATED_IN_XERO';

/** What the client record card needs. `hidden` renders nothing at all. */
export type ClientInvoicingLinkState =
  | { kind: 'hidden' }
  | {
      kind: 'route-not-ready';
      providerLabel: string;
      wizardHref: string;
    }
  | {
      kind: 'linked';
      providerLabel: string;
      connectionName: string | null;
      /** Null when the contact is not in the latest contact refresh. */
      contactLabel: string | null;
    }
  | {
      kind: 'unlinked';
      providerLabel: string;
      connectionId: string;
      connectionName: string | null;
      /** An existing mapping that still needs confirming, if any. */
      pendingContactLabel: string | null;
      suggestedContactId: string | null;
      suggestedMatchMethod: ContactMatchMethod;
      options: AccountingLinkOption[];
      optionsTruncated: boolean;
      wizardHref: string;
    };

export type WorkerPayrollLinkState =
  | { kind: 'hidden' }
  | {
      kind: 'linked';
      providerLabel: string;
      connectionName: string | null;
      employeeLabel: string | null;
      /** False when the latest employee refresh could not be checked. */
      verified: boolean;
    }
  | {
      kind: 'unlinked';
      providerLabel: string;
      connectionId: string;
      connectionName: string | null;
      /** A confirmed link exists but its employee is archived or gone. */
      previousLinkBroken: boolean;
      suggestedEmployeeId: string | null;
      suggestedMatchMethod: 'AUTO_EMAIL' | 'AUTO_NAME' | 'MANUAL';
      employees: Array<AccountingLinkOption & { payrollCalendarId: string | null }>;
      payrollCalendars: AccountingLinkOption[];
      defaultPayrollCalendarId: string | null;
      wizardHref: string;
    };

export interface AccountingLinkPrompts {
  invoicing: boolean;
  payroll: boolean;
  providerLabel: string | null;
}

export const NO_ACCOUNTING_LINK_PROMPTS: AccountingLinkPrompts = {
  invoicing: false,
  payroll: false,
  providerLabel: null,
};

/** Provider-neutral wording for the errors the reused actions return. */
export function accountingLinkErrorMessage(error: string | null | undefined): string {
  const code = String(error ?? '');
  if (code === 'forbidden') return 'Only finance or payroll administrators can change this link.';
  if (code === 'connection_unavailable')
    return 'The accounting connection is not available. Reconnect it in Settings, then try again.';
  if (code === 'xero_quota_headroom_reached_retry_later')
    return 'The accounting system is busy. Try again in a few minutes.';
  if (code === 'client_not_found') return 'This client could not be found.';
  if (code === 'xero_employee_mapping_payroll_calendar_required')
    return 'Choose a pay calendar before linking this worker.';
  if (code.startsWith('xero_contact_create_failed'))
    return 'The accounting system refused the new contact. Check the client’s name and email, then try again.';
  if (code === 'xero_contact_no_id_returned')
    return 'The accounting system did not confirm the new contact. Refresh the wizard data before trying again.';
  return 'The link could not be saved. Try again, or use the setup wizard.';
}

interface XeroContactLike {
  ContactID?: string;
  Name?: string;
  FirstName?: string;
  LastName?: string;
  EmailAddress?: string;
}

/** Same label the wizard contact step shows. */
export function contactOptionLabel(xeroId: string, payload: XeroContactLike | null): string {
  const p = payload ?? {};
  const name = p.Name?.trim() || [p.FirstName, p.LastName].filter(Boolean).join(' ').trim();
  return p.EmailAddress
    ? `${name || p.ContactID || xeroId} (${p.EmailAddress})`
    : name || p.ContactID || xeroId;
}

interface XeroEmployeeLike {
  FirstName?: string;
  LastName?: string;
  Email?: string;
  PayrollCalendarID?: string;
}

export function employeeOptionLabel(xeroId: string, payload: XeroEmployeeLike | null): string {
  const p = payload ?? {};
  const name = [p.FirstName, p.LastName].filter(Boolean).join(' ').trim() || xeroId;
  return p.Email ? `${name} (${p.Email})` : name;
}

function normaliseName(value: string | null | undefined): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Suggest one existing contact: exact email first, then exact full name. The
 * operator always confirms; a suggestion is never saved on its own.
 */
export function suggestContact(
  person: { firstName: string | null; lastName: string | null; email: string | null },
  contacts: ReadonlyArray<{ xeroId: string; payload: XeroContactLike | null }>,
): { xeroId: string; matchMethod: 'AUTO_EMAIL' | 'AUTO_NAME' } | null {
  const email = normaliseName(person.email);
  if (email) {
    const hit = contacts.find((c) => normaliseName(c.payload?.EmailAddress) === email);
    if (hit) return { xeroId: hit.xeroId, matchMethod: 'AUTO_EMAIL' };
  }
  const name = normaliseName([person.firstName, person.lastName].filter(Boolean).join(' '));
  if (name) {
    const hit = contacts.find((c) => {
      const p = c.payload ?? {};
      const contactName = normaliseName(
        p.Name?.trim() || [p.FirstName, p.LastName].filter(Boolean).join(' '),
      );
      return contactName === name;
    });
    if (hit) return { xeroId: hit.xeroId, matchMethod: 'AUTO_NAME' };
  }
  return null;
}

export function suggestEmployee(
  person: { firstName: string | null; lastName: string | null; email: string | null },
  employees: ReadonlyArray<{ xeroId: string; payload: XeroEmployeeLike | null }>,
): { xeroId: string; matchMethod: 'AUTO_EMAIL' | 'AUTO_NAME' } | null {
  const email = normaliseName(person.email);
  if (email) {
    const hit = employees.find((e) => normaliseName(e.payload?.Email) === email);
    if (hit) return { xeroId: hit.xeroId, matchMethod: 'AUTO_EMAIL' };
  }
  const name = normaliseName([person.firstName, person.lastName].filter(Boolean).join(' '));
  if (name) {
    const hit = employees.find(
      (e) =>
        normaliseName([e.payload?.FirstName, e.payload?.LastName].filter(Boolean).join(' ')) ===
        name,
    );
    if (hit) return { xeroId: hit.xeroId, matchMethod: 'AUTO_NAME' };
  }
  return null;
}
